import { useCallback, useMemo, useState } from "react";
import { useObrasConcretagemPagination } from "../hooks/Obras_concretagem/pagination";
import { MapaConcretagem } from "../../domain/models/mapa_concretagem";
import { initialObrasConcretagemFilters, useObrasConcretagemFilter } from "../hooks/Obras_concretagem/filters";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useLocation } from "react-router";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";

import dayjs from "dayjs";

/**
 * LoadMapaConcretagensFuncState
 */

type LoadMapaConcretagensFuncState = {
  idObra?: number;
  dataDe?: Date | null;
  dataAte?: Date | null;
  localLancamento?: string | null;
  resistencia?: string | null;
  nroNotaFiscal?: string | null;
  statusRompimento?: number | null;
  enviadoLab?: number | null;
  nroLaudo?: string | null;
  aprovado?: number | null;
  sortingModel?: GridSortModel | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useObrasConcretagemViewModel
 */

export const useObrasConcretagemViewModel = () => {
  const {
    commons: {
      getFuncionarioByIdUsuarioUseCase,
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    obras: {
      getAllMapaConcretagemUseCase,
      getObraHeaderInfoUseCase,
      deleteMapaConcretagemUseCase,
      copyMapaConcretagemUseCase,
      aprovacaoFinalMapaConcretagemUseCase,
    },
  } = useDependencyInjection();

  const {
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
  } = usePageLoader();

  const debouncedRef = useCreateDebounceHandler(750);

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { idUsuario } = useUserAuthStore();

  const { setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idFuncionarioAprovacao, setIdFuncionarioAprovacao] = useState<number | null>(null);
  const [idObra, setIdObra] = useState<number>();

  const [fornecedoresDropdown, setFornecedoresDropdown] = useState<FornecedorDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();

  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();
  const [selectedMapaConcretagem, setSelectedMapaConcretagem] = useState<MapaConcretagem>();

  const [mapaConcretagens, setMapaConcretagens] = useState<MapaConcretagem[]>();

  const [aprovFinalModal, setAprovFinalModal] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const [sortingModel, setSortingModel] = useState<GridSortModel>();

  const [openMapaConcretagemDeleteModal, setOpenMapaConcretagemDeleteModal] = useState(false);
  const [openMapaConcretagemCopyModal, setOpenMapaConcretagemCopyModal] = useState(false);

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: setErrorMessage,
  });

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra });

  const filters = useObrasConcretagemFilter();
  const pagination = useObrasConcretagemPagination();

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => x.canBeSelected);
  }, [
    funcionariosDropdown,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadMapaConcretagens = useCallback(async (fs?: LoadMapaConcretagensFuncState) => {
    try {
      const filteredIdObra = fs?.idObra ?? idObra;
      const filteredDataDe = evaluateParamValue(fs?.dataDe, filters.dataDe);
      const filteredDataAte = evaluateParamValue(fs?.dataAte, filters.dataAte);
      const filteredLocalLancamento = evaluateParamValue(fs?.localLancamento, filters.localLancamento);
      const filteredResistencia = evaluateParamValue(fs?.resistencia, filters.resistencia);
      const filteredNroNotaFiscal = evaluateParamValue(fs?.nroNotaFiscal, filters.nroNotaFiscal);
      const filteredStatusRompimento = evaluateParamValue(fs?.statusRompimento, filters.statusRompimento);
      const filteredEnviadoLab = evaluateParamValue(fs?.enviadoLab, filters.enviadoLab);
      const filteredNroLaudo = evaluateParamValue(fs?.nroLaudo, filters.nroLaudo);
      const filteredAprovado = evaluateParamValue(fs?.aprovado, filters.aprovado);
      const filteredSortingModel = evaluateParamValue(fs?.sortingModel, sortingModel);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllMapaConcretagemUseCase.getAllMapaConcretagem(
        filteredIdObra,
        filteredDataDe,
        filteredDataAte,
        filteredLocalLancamento,
        filteredResistencia,
        filteredNroNotaFiscal,
        filteredStatusRompimento,
        filteredEnviadoLab,
        filteredNroLaudo,
        filteredAprovado,
        filteredSortingModel,
        filteredPage,
        filteredRowsPerPage,
      );

      setMapaConcretagens(getAllMapaConcretagemUseCase.items);

      pagination.setTotalItems(getAllMapaConcretagemUseCase.totalItems);
    } catch (e: unknown) {
      setMapaConcretagens([]);
      throw e;
    }
  }, [
    idObra,
    filters,
    pagination,
    getAllMapaConcretagemUseCase,
    sortingModel,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(id));

      const response = await getFuncionarioByIdUsuarioUseCase
        .getFuncionarioByIdUsuario(idUsuario);

      if (response !== null) {
        setIdFuncionarioAprovacao(response.idFuncionario);
      }

      await loadObraHeaderInfo(Number(id));

      await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadObrasCommonReportsData(Number(id));

      await loadMapaConcretagens({ idObra: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados das paginas!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idObra) {
      try {
        setLoading(true);

        const response = await getFuncionarioByIdUsuarioUseCase
          .getFuncionarioByIdUsuario(idUsuario);

        if (response !== null) {
          setIdFuncionarioAprovacao(response.idFuncionario);
        }

        await loadObraHeaderInfo(idObra);

        await loadFornecedoresDropDown(dt => setFornecedoresDropdown(dt));

        await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

        await loadObrasCommonReportsData(idObra);
        await loadMapaConcretagens();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
    loadObraHeaderInfo,
    loadObrasCommonReportsData,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleMapaConcretagemOpenStateModal = useCallback(async (idMapaConcretagem: number | null, openNewTab: boolean) => {
    try {
      setLoading(true);
      if (idMapaConcretagem) {
        if (openNewTab) {
          window.open(`/ObrasConcretagem_cadastro?idObra=${idObra}&id=${idMapaConcretagem}`, '_blank', 'noopener,noreferrer');
        } else {
          backOrNavigate(`/ObrasConcretagem_cadastro?idObra=${idObra}&id=${idMapaConcretagem}`, { alternativeRoute: `/ChecklistMaquinaEquip_lista` });
        }
      } else {
        if (openNewTab) {
          window.open(`/ObrasConcretagem_cadastro?idObra=${idObra}`, '_blank', 'noopener,noreferrer');
        } else {
          backOrNavigate(`/ObrasConcretagem_cadastro?idObra=${idObra}`, { alternativeRoute: `/ChecklistMaquinaEquip_lista` });
        }
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar mapa de concretagem!'));
    } finally {
      setLoading(false);
    }
  }, [
    idObra,
    backOrNavigate,
    pushErrorNotification,
  ]);



  const handleDeleteMapaConcretagemOpenStateModal = useCallback((idMapaConcretagem: number | null, openState: boolean) => {
    if (openState) {
      if (idMapaConcretagem && mapaConcretagens) {
        setSelectedMapaConcretagem(mapaConcretagens.find(x => x.idMapaConcretagem === idMapaConcretagem));
      } else {
        setSelectedMapaConcretagem(undefined);
      }
    }
    setOpenMapaConcretagemDeleteModal(openState);
  }, [
    mapaConcretagens,
  ]);

  const handleDeleteMapaConcretagemConfirm = useCallback(async () => {
    if (selectedMapaConcretagem) {
      try {
        setPageSaving(true);
        await deleteMapaConcretagemUseCase.deleteMapaConcretagem(
          selectedMapaConcretagem.idMapaConcretagem,
        );
        await loadMapaConcretagens();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
        setSelectedMapaConcretagem(undefined);
        setOpenMapaConcretagemDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao salvar mapa de concretagem!'));
      } finally {
        setPageSaving(false);
      }
    }
  }, [
    selectedMapaConcretagem,
    deleteMapaConcretagemUseCase,
    loadMapaConcretagens,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);



  const handleMapaConcretagemCopyOpenStateModal = useCallback((idMapaConcretagem: number | null, openState: boolean) => {
    if (openState) {
      if (idMapaConcretagem && mapaConcretagens) {
        setSelectedMapaConcretagem(mapaConcretagens.find(x => x.idMapaConcretagem === idMapaConcretagem));
      } else {
        setSelectedMapaConcretagem(undefined);
      }
    }
    setOpenMapaConcretagemCopyModal(openState);
  }, [
    mapaConcretagens,
  ]);

  const handleMapaConcretagemCopyConfirm = useCallback(async (copyNumber: number) => {
    if (selectedMapaConcretagem) {
      try {
        setLoading(true);
        setPageSaving(true);

        await copyMapaConcretagemUseCase.copyMapaConcretagem(
          selectedMapaConcretagem.idMapaConcretagem,
          copyNumber,
        );

        await loadMapaConcretagens();
        pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao copiar mapa de concretagem!'));
      } finally {
        setLoading(false);
        setPageSaving(false);
        setOpenMapaConcretagemCopyModal(false);
      }
    }
  }, [
    selectedMapaConcretagem,
    copyMapaConcretagemUseCase,
    loadMapaConcretagens,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleAprovFinalSelectChange = useCallback((rowSelection: GridRowSelectionModel) => {
    setRowSelectionModel(rowSelection);
  }, [
  ]);

  const handleAprovFinalModal = useCallback(async (openState: boolean) => {
    if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
      setAprovFinalModal(openState);
    } else {
      pushWarnNotification("Atenção", "Selecione ao menos um registro para aprovar!");
    }
  }, [
    rowSelectionModel,
    pushWarnNotification,
  ]);

  const handleAprovFinalSubmit = useCallback(async (idFuncionario: number) => {
    try {
      setSaving(true);
      setPageSaving(true);

      await aprovacaoFinalMapaConcretagemUseCase.executar(
        idFuncionario, dayjs.utc().toDate(), Array.from(rowSelectionModel?.ids ?? []).map(x => Number(x)));

      await loadMapaConcretagens();

      pushInfoNotification('Sucesso', 'Registros aprovados com sucesso!');

      setRowSelectionModel({ type: 'include', ids: new Set() });
      setAprovFinalModal(false);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    rowSelectionModel,
    aprovacaoFinalMapaConcretagemUseCase,
    loadMapaConcretagens,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleSortingModelChange = useCallback(async (newSortingModel: GridSortModel) => {
    try {
      setLoading(true);
      setSortingModel(newSortingModel);
      await loadMapaConcretagens({ sortingModel: newSortingModel });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadMapaConcretagens,
  ]);

  const handleDataDeChange = useDebouncedCallback(debouncedRef, (dataDe: Date | null) => {
    useObrasConcretagemFilter.setState({ dataDe });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ dataDe, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleDataAteChange = useDebouncedCallback(debouncedRef, (dataAte: Date | null) => {
    useObrasConcretagemFilter.setState({ dataAte });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ dataAte, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleLocalLancamentoChange = useDebouncedCallback(debouncedRef, (localLancamento: string | null) => {
    useObrasConcretagemFilter.setState({ localLancamento });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ localLancamento, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleResistenciaChange = useDebouncedCallback(debouncedRef, (resistencia: string | null) => {
    useObrasConcretagemFilter.setState({ resistencia });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ resistencia, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleNroNotaFiscalChange = useDebouncedCallback(debouncedRef, (nroNotaFiscal: string | null) => {
    useObrasConcretagemFilter.setState({ nroNotaFiscal });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ nroNotaFiscal, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleNroLaudoChange = useDebouncedCallback(debouncedRef, (nroLaudo: string | null) => {
    useObrasConcretagemFilter.setState({ nroLaudo });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ nroLaudo, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleStatusRompimentoChange = useDebouncedCallback(debouncedRef, (statusRompimento: number | null) => {
    useObrasConcretagemFilter.setState({ statusRompimento });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ statusRompimento, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleEnviadoLabChange = useDebouncedCallback(debouncedRef, (enviadoLab: number | null) => {
    useObrasConcretagemFilter.setState({ enviadoLab });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ enviadoLab, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleAprovadoChange = useDebouncedCallback(debouncedRef, (aprovado: number | null) => {
    useObrasConcretagemFilter.setState({ aprovado });
    return async () => {
      try {
        setLoading(true);
        useObrasConcretagemPagination.setState({ page: 1 });
        await loadMapaConcretagens({ aprovado, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleClearFilter = useCallback(async () => {
    try {
      setLoading(true);
      useObrasConcretagemPagination.setState({ page: 1 });
      useObrasConcretagemFilter.getState().clearFilters();
      await loadMapaConcretagens({ ...initialObrasConcretagemFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObrasConcretagemPagination.setState({ page: page + 1 });
      await loadMapaConcretagens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObrasConcretagemPagination.setState({ page: 1, rowsPerPage });
      await loadMapaConcretagens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMapaConcretagens,
    pushErrorNotification,
  ]);

  /**
   * History
   */

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idObra) {
        changeHistory.handleOpenChangeHistory(idObra, LogTipoTabela.OBRA, true);
      }
    }
  }, [
    idObra,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,

    obrasTopHeaderState,
    obrasReportState,

    idFuncionarioAprovacao,
    idObra,

    selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,
    selectedMapaConcretagem: selectedMapaConcretagem ?? null,
    mapaConcretagens: mapaConcretagens ?? [],
    aprovFinalModal,
    rowSelectionModel,

    sortingModel,

    openMapaConcretagemDeleteModal,
    openMapaConcretagemCopyModal,

    changeHistory,

    filters,
    pagination,

    fornecedoresDropdown: fornecedoresDropdown ?? [],
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],

    handleBack,
    handleReload,
    handleMapaConcretagemOpenStateModal,
    handleDeleteMapaConcretagemOpenStateModal,
    handleDeleteMapaConcretagemConfirm,
    handleMapaConcretagemCopyOpenStateModal,
    handleMapaConcretagemCopyConfirm,
    handleAprovFinalSelectChange,
    handleAprovFinalModal,
    handleAprovFinalSubmit,
    handleSortingModelChange,
    handleDataDeChange,
    handleDataAteChange,
    handleLocalLancamentoChange,
    handleResistenciaChange,
    handleNroNotaFiscalChange,
    handleNroLaudoChange,
    handleStatusRompimentoChange,
    handleEnviadoLabChange,
    handleAprovadoChange,
    handleClearFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenChangeHistory,
  };
}